import React, { useState, useEffect, useRef } from 'react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

function CompanyAutocomplete({ value, onChange, disabled }) {
  const [inputValue, setInputValue] = useState(value || '');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loading, setLoading] = useState(false);
  const wrapperRef = useRef(null);
  const debounceTimer = useRef(null);

  // Close suggestions when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fetch suggestions from backend
  const fetchSuggestions = async (query) => {
    if (query.length < 2) {
      setSuggestions([]);
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/search/companies?q=${encodeURIComponent(query)}`
      );
      
      if (response.ok) {
        const data = await response.json();
        setSuggestions(data.results || []);
        setShowSuggestions(true);
      }
    } catch (error) {
      console.error('Company search error:', error);
      setSuggestions([]);
    } finally {
      setLoading(false);
    }
  };

  // Handle input change with debounce
  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    onChange(newValue);

    // Clear previous timer
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    // Debounce API call
    debounceTimer.current = setTimeout(() => {
      fetchSuggestions(newValue);
    }, 300);
  };

  // Handle suggestion selection
  const handleSelectSuggestion = (suggestion) => {
    const displayValue = `${suggestion.name} (${suggestion.symbol})`;
    setInputValue(displayValue);
    onChange(displayValue);
    setShowSuggestions(false);
    setSuggestions([]);
  };

  return (
    <div ref={wrapperRef} className="relative">
      <input
        id="company"
        data-testid="company-input"
        type="text"
        value={inputValue}
        onChange={handleInputChange}
        onFocus={() => {
          if (suggestions.length > 0) {
            setShowSuggestions(true);
          }
        }}
        placeholder="e.g., Apple, TSLA, Microsoft"
        disabled={disabled}
        required
        autoComplete="off"
        className="w-full px-4 py-3 rounded-lg text-sm"
        style={{
          backgroundColor: 'white',
          border: '2px solid hsl(var(--gb-gold))',
          color: 'hsl(var(--gb-navy))'
        }}
      />

      {/* Suggestions Dropdown */}
      {showSuggestions && suggestions.length > 0 && (
        <div
          className="absolute z-50 w-full mt-1 rounded-lg shadow-lg overflow-hidden"
          style={{
            backgroundColor: 'white',
            border: '1px solid hsl(var(--gb-border))',
            maxHeight: '300px',
            overflowY: 'auto'
          }}
        >
          {suggestions.map((suggestion, index) => (
            <button
              key={`${suggestion.symbol}-${index}`}
              type="button"
              onClick={() => handleSelectSuggestion(suggestion)}
              className="w-full text-left px-4 py-3 hover:bg-opacity-50 transition-colors"
              style={{
                borderBottom: index < suggestions.length - 1 ? '1px solid hsl(var(--gb-border))' : 'none',
                backgroundColor: 'transparent'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'hsl(var(--gb-surface-2))';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              <div className="flex justify-between items-center">
                <div>
                  <div 
                    className="font-semibold text-sm"
                    style={{ color: 'hsl(var(--gb-navy))' }}
                  >
                    {suggestion.name}
                  </div>
                  <div 
                    className="text-xs"
                    style={{ color: 'hsl(var(--gb-text-muted))' }}
                  >
                    {suggestion.type}
                  </div>
                </div>
                <div
                  className="text-xs font-mono font-semibold px-2 py-1 rounded"
                  style={{
                    backgroundColor: 'hsl(var(--gb-surface-2))',
                    color: 'hsl(var(--gb-navy))'
                  }}
                >
                  {suggestion.symbol}
                </div>
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Loading indicator */}
      {loading && (
        <div
          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-xs"
          style={{ color: 'hsl(var(--gb-text-muted))' }}
        >
          Searching...
        </div>
      )}
    </div>
  );
}

export default CompanyAutocomplete;
